import React from 'react'
import { cn } from './cn'

export interface SettingRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Left-column label (i18n at the call site). */
  label: React.ReactNode
  /** Optional muted sub-line under the label. */
  description?: React.ReactNode
  /** Right-column control (Switch / Select / Button / …). */
  control: React.ReactNode
  /** Optional full-width detail below label/control (help text, chips, billing). */
  supportingContent?: React.ReactNode
  /**
   * Rule above every row and one below the last, closing the group top and
   * bottom (the App surface settings page). Default: rules between rows only.
   */
  enclosed?: boolean
  /**
   * Icon or mark in its own first column, aligned to the title line (設計側 `.srow .lead`).
   * At least 20px; a larger mark (a 28px avatar) keeps its own size.
   */
  lead?: React.ReactNode
}

/**
 * 設定列：左側標籤（含選用描述）、右側控制元件。字級、線色、最小高與左右間距讀
 * `--*-row` 那組角色 token，網頁與 App 面各自的值寫在 kiao-tokens.css；分隔線畫在
 * 哪裡是結構差異，由 `enclosed` 決定。
 */
export function SettingRow({
  label,
  description,
  control,
  supportingContent,
  enclosed = false,
  lead,
  className,
  ...rest
}: SettingRowProps) {
  return (
    <div
      data-setting-row
      {...rest}
      className={cn(
        'grid min-h-[var(--min-h-row)] items-center gap-x-[var(--gap-row)] gap-y-[var(--gap-sm)] border-[color:var(--line-row)] py-[var(--gap-sm)]',
        lead == null ? 'grid-cols-[minmax(0,1fr)_auto]' : 'grid-cols-[auto_minmax(0,1fr)_auto]',
        enclosed ? 'border-t last:border-b' : 'border-b last:border-b-0',
        className
      )}
    >
      {lead == null ? null : (
        <span
          data-setting-row-lead
          aria-hidden="true"
          className="flex min-h-5 min-w-5 items-center justify-center self-start text-foreground [&>svg]:size-[1.125rem]"
        >
          {lead}
        </span>
      )}
      <span className="min-w-0 text-[length:var(--fs-m-row)] leading-[var(--leading-row-title)]">
        {label}
        {description ? (
          <span className="mt-0.5 block text-xs leading-[var(--leading-row-desc)] text-muted">
            {description}
          </span>
        ) : null}
      </span>
      {control}
      {supportingContent != null ? (
        <div className={lead == null ? 'col-span-2' : 'col-span-2 col-start-2'}>
          {supportingContent}
        </div>
      ) : null}
    </div>
  )
}
